import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Scissors, LogOut, Plus, Pencil, Trash2, Package, ClipboardList, BarChart3, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORY_LABELS, CATEGORY_BADGE, formatPrice } from "@/api";
import MachineForm from "@/components/admin/MachineForm";
import OrdersPanel from "@/components/admin/OrdersPanel";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("machines");
  const [machines, setMachines] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin"));
  }, [navigate]);

  const loadMachines = () => api.get("/machines").then((r) => setMachines(r.data)).catch(() => {});
  const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadStats = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    if (user) { loadMachines(); loadOrders(); loadStats(); }
  }, [user]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare "${m.name}" dal catalogo?`)) return;
    try {
      await api.delete(`/machines/${m.id}`);
      toast.success("Macchina eliminata");
      loadMachines();
      loadStats();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const refreshAll = () => { loadMachines(); loadOrders(); loadStats(); };

  if (!user) {
    return <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="admin-loading">
      <div className="w-10 h-10 rounded-full border-4 border-sky-200 border-t-sky-600 animate-spin" />
    </div>;
  }

  const tabs = [
    { id: "machines", label: "Gestione Macchine", icon: Package },
    { id: "orders", label: "Richieste d'Ordine", icon: ClipboardList, badge: orders.filter((o) => o.status === "da_contattare").length },
    { id: "stats", label: "Riepilogo", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-dashboard">
      <header className="bg-white/80 backdrop-blur-xl border-b border-sky-100 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.jpg" alt="Morselli Renzo" className="h-9 w-auto" />
            <div className="leading-tight">
              <span className="block font-serif font-semibold text-lg text-slate-900">Dashboard Morselli</span>
              <span className="block text-[10px] uppercase tracking-[0.18em] text-sky-700">{user.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-sky-700 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Vedi sito
            </Link>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button" className="rounded-full gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-sky-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-sky-300 hover:text-sky-700"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${tab === t.id ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"}`} data-testid="orders-pending-badge">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "machines" && (
          <div data-testid="machines-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-semibold text-slate-900">Macchine in negozio ({machines.length})</h2>
              <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-machine-button"
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-full gap-1.5">
                <Plus className="w-4 h-4" /> Aggiungi Macchina
              </Button>
            </div>
            {machines.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center" data-testid="machines-empty">Nessuna macchina in catalogo. Aggiungi la prima!</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {machines.map((m) => (
                  <div key={m.id} className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden" data-testid={`admin-machine-card-${m.id}`}>
                    <div className="aspect-[4/3] bg-sky-50/50">
                      <img src={m.photos?.[0]} alt={m.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${CATEGORY_BADGE[m.category] || ""}`}>
                        {CATEGORY_LABELS[m.category] || m.category}
                      </span>
                      <h3 className="font-semibold text-slate-900 mt-2 leading-snug">{m.name}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">{m.brand} {m.model && `· ${m.model}`}</p>
                      <p className="text-sm mt-2">
                        <span className="font-semibold text-sky-700">{formatPrice(m.price)}</span>
                        <span className="text-slate-400"> · {m.stock} pz disponibili</span>
                      </p>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1.5"
                          data-testid={`edit-machine-${m.id}`} onClick={() => { setEditing(m); setFormOpen(true); }}>
                          <Pencil className="w-3.5 h-3.5" /> Modifica
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                          data-testid={`delete-machine-${m.id}`} onClick={() => remove(m)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <OrdersPanel orders={orders} filter={orderFilter} setFilter={setOrderFilter} onRefresh={() => { loadOrders(); loadStats(); }} />
        )}

        {tab === "stats" && stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-panel">
            {[
              { label: "Macchine in Catalogo", value: stats.total_machines, testid: "stat-total-machines" },
              { label: "Richieste da Gestire", value: stats.pending_orders, testid: "stat-pending-orders" },
              { label: "Richieste Totali", value: stats.total_orders, testid: "stat-total-orders" },
              { label: "Scorte in Esaurimento", value: stats.low_stock, testid: "stat-low-stock" },
            ].map((s) => (
              <div key={s.testid} className="bg-white rounded-2xl border border-slate-200/80 p-6" data-testid={s.testid}>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-medium">{s.label}</p>
                <p className="font-serif text-4xl font-semibold text-slate-900 mt-2">{s.value}</p>
              </div>
            ))}
            {stats.top_machine && (
              <div className="sm:col-span-2 lg:col-span-4 bg-sky-50 border border-sky-100 rounded-2xl p-6" data-testid="stat-top-machine">
                <p className="text-xs uppercase tracking-[0.15em] text-sky-700 font-medium">Macchina più richiesta</p>
                <p className="font-serif text-2xl font-semibold text-slate-900 mt-1">{stats.top_machine}</p>
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="machine-form-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editing ? "Modifica Macchina" : "Nuova Macchina"}</DialogTitle>
          </DialogHeader>
          <MachineForm initial={editing} onSaved={() => { setFormOpen(false); setEditing(null); refreshAll(); }} onCancel={() => { setFormOpen(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
